import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CalendarOff, ChevronDown, Plus, Trash2 } from 'lucide-react';
import cx from 'classnames';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Checkbox } from '@ui/components/ui/checkbox';
import { runViewTransition } from '@app/components/core/viewTransition';
import { dayKey } from '@app/utils/helpers/date-utils';
import { cardHeaderClass, cardHeaderTitleClass, cardShellClass } from '@app/constants/styles';
import SprintScrollRail from './SprintScrollRail';
import { asAmount, formatBudgetHours, formatEur, personCost, sumCosts } from './budget';
import { openingSprint } from './openingSprint';
import { useReportUnsaved } from './unsavedWork';
import { asRate, newFractionalId, resolveRoster, type FractionalDraft, type FractionalPerson } from './fractional';
import DiscountNote from './DiscountNote';
import ReleaseDialog from './ReleaseDialog';
import { heldBack } from './retention';
import { type CopyUnit, moneyField, numberFieldHolds, rangeContainer, rangeOverlay, useCellRange } from './cellRange';
import { useEdgeBounce } from './edgeBounce';
import CellMenu from './CellMenu';
import { isFolded, readFolds, rememberFolds, setFold } from './chapterFolds';
import { pointAtCentre, pointInView, rememberPointInView, scrollToPoint } from './gridScroll';
import { EXTRA_CHARGE, asTyped, typedAdjustment, type Discount, type SprintDiscounts } from './discounts';
import {
  gridHeadTint,
  hoursFoldBox,
  hoursFoldOpen,
  hoursFoldShut,
  hoverDarken,
  hoverDarkenRow,
  hoursCell,
  hoursCellFlush,
  hoursColumns,
  hoursEndCell,
  hoursEndStrong,
  hoursHeadCell,
  hoursNameCell,
  hoursNameFlush,
  hoursNameWidth,
  hoursRow,
  hoursTotalName,
  actualTint,
  actualTintStrong,
  projectedTint,
  projectedTintStrong,
} from './sprintGrid';
import { formatSprintRange } from '../../clientReporting/product/sources/sprintRange';

const dash = '—';

/** The discount row's stand-in for a person, so one cell at a time covers it too. */
const DISCOUNT_ROW = '__discount__';
const FIXED_ROW = '__fixed__';
const RETENTION_ROW = '__retention__';
const RELEASE_ROW = '__release__';

/** Part of the block so the whole table copies at once; none of them can be typed into. */
const HEAD_ROW = '__head__';
/** A chapter's own total line, shown while the breakdown is on. */
const chapterRow = (key: string): string => `__chapter__:${key}`;
const TOTAL_ROW = '__total__';
const NAME_COL = '__member__';
const TOTAL_COL = '__yearTotal__';
/** A block reaches across it to stay one shape, but there is nothing in it to copy. */
const ADD_ROW = '__addFractional__';

/** base-ui focuses the mark on hover, and a portal's events travel the React tree, so both reach here. */
const ownEvent = (event: React.SyntheticEvent): boolean =>
  !(event.target as HTMLElement).closest('[data-discount-note]');

const hintText = 'text-sm text-muted-foreground';
// Swapped at the app's own beat: changed between frames it read as a different card.
const hintFade = 'animate-in fade-in duration-[180ms] motion-reduce:animate-none';

/** A fractional row's own price, or a dash where nobody has agreed one yet. */
const rateLabel = (rate: number | null): string => (rate === null ? dash : `€${formatBudgetHours(rate)}/h`);

/** Which of a cell's two figures takes the line. Hours are what is stored; euros are those hours priced. */
type GridUnit = 'hours' | 'euros';

/** On the grid, not the cell classes: the out of pocket costs share them and carry one figure. */
const twoFigureRow = { '--row-h': 'var(--two-figure-row-h)' } as React.CSSProperties;
/** A forecast's head row, a line taller for the word under each sprint's dates. */
const forecastHeadRow = { '--row-h': 'calc(var(--two-figure-row-h) + 0.875rem)' } as React.CSSProperties;

/** Both at once: a cost and the hours behind it are read together. */
const Figures = ({
  lead,
  under,
  className,
  underClassName = 'text-muted-foreground/70',
}: {
  lead: string;
  under?: string;
  className?: string;
  /** The figure underneath, where it carries a meaning of its own rather than being the quiet half. */
  underClassName?: string;
}): JSX.Element => (
  <span className={cx('flex flex-col items-end whitespace-nowrap leading-tight tabular-nums', className)}>
    <span>{lead}</span>
    {!!under && <span className={cx('text-xs font-normal max-md:text-[0.6875rem]', underClassName)}>{under}</span>}
  </span>
);

export interface GridPerson {
  id: string;
  name: string;
}

export interface GridSprint {
  id: string;
  number: number;
  /** Without these the grid opens where a scroll box does by itself, on the first column. */
  startDate?: string;
  /** With the start, it prints the sprint's dates under its number. */
  endDate?: string;
}

/** A chapter and the people filed under it: the rows it names, in the order they are drawn. */
export interface GridGroup {
  key: string;
  label: string;
  people: GridPerson[];
}

/** The two blocks the people fall into while the chapter breakdown is off. */
const ACTIVE_GROUP = '__active__';
const INACTIVE_GROUP = '__inactive__';

/** Hours by sprint, then by person: the shape the grid is read from and saved back as. */
export type SprintHours = Record<string, Record<string, number>>;

/** One grid, so one scrollbar moves every chapter to the same sprint; read-only without `onSave`. */
const WorkforceGrid = ({
  gridId,
  title,
  hint,
  groups,
  nameLabel,
  emptyNote,
  controls,
  cornerControls,
  sprints,
  hours,
  onSave,
  rateFor,
  costs,
  fractional,
  editableRows = 'all',
  openSprintIds,
  lead = 'hours',
  discounts,
  fixedCharges,
  retention,
  totalColumn = false,
  grouped = false,
  projectedSprintIds,
  typeableSprintIds,
  hidesEmptySprints = true,
  totalActualsOnly = false,
  totalNote,
  reading,
  activeIds,
  opensOn: askedFor,
}: {
  /** A block selection belongs to the grid it was drawn on and to nothing else. */
  gridId: string;
  /** A sprint to centre on arrival in place of today's, when it is one of the columns. */
  opensOn?: string;
  title: string;
  /** Only where there is something to say the grid does not: nothing to explain is a line of space back. */
  hint?: string;
  groups: GridGroup[];
  /** What the first column holds, where it is not the product's members. */
  nameLabel?: string;
  /** What the card says instead of a table when there is nothing to put in one. */
  emptyNote?: string;
  /** A control of the caller's own, in the card's header beside the grid's. */
  controls?: React.ReactNode;
  /** The same, after the grid's own, in the card's corner. */
  cornerControls?: React.ReactNode;
  /** The year, oldest sprint first. */
  sprints: GridSprint[];
  hours: SprintHours;
  /** Absent for a grid nobody types into. The roster comes back with the hours: Edit holds both. */
  onSave?: (hours: SprintHours, fractional: FractionalPerson[]) => void;
  /** Without it there is nothing to price with, and only hours are shown. */
  rateFor?: (sprintId: string, personId: string) => number | null;
  /** Each row's euros as already priced, in place of `rateFor`, for rows that are not people. */
  costs?: SprintHours;
  /** Named and priced in the grid itself: nobody registers them, so the cost matrix cannot price them. */
  fractional?: { key: string; label: string; people: FractionalPerson[] };
  /** Spend's team rows are already-reported hours, so only its fractional block is typed into. */
  editableRows?: 'all' | 'fractional';
  /** Sprints this product has not closed: their bills have not gone out, so money is released into them. */
  openSprintIds?: string[];
  /** A tab that is filled in leads with hours; Spend is read, and what is read off it is money. */
  lead?: GridUnit;
  /** Each row summed over every sprint, in a column after the last one. */
  totalColumn?: boolean;
  /** Every block keeps its heading and starts folded, with no breakdown to switch. */
  grouped?: boolean;
  /** A forecast: these sprints hold the projection and the rest what was spent, each column tinted to say which. */
  projectedSprintIds?: string[];
  /** Sprints every row can be typed into, beyond what `editableRows` allows across the whole year. */
  typeableSprintIds?: string[];
  /** Offers to hide the sprints nobody worked; the costs tab's reading menu does that job there. */
  hidesEmptySprints?: boolean;
  /** The year's total counts only the sprints that are not projected. */
  totalActualsOnly?: boolean;
  /** Under the total's heading: what it adds up. */
  totalNote?: string;
  /** Which way the figures are read; a draft typed into one reading is never saved as another. */
  reading?: string;
  /** Members on the product now: with the breakdown off they lead, and everyone else folds away under them. */
  activeIds?: Set<string>;
  /** Budget passes none: a budget is what work is expected to cost, not what is billed. */
  discounts?: {
    values: SprintDiscounts;
    label: string;
    /** Absent for a reader who may not change them: the row still shows what came off. */
    onSave?: (next: SprintDiscounts) => void;
  };
  /** What a fixed-price version charges each sprint, in place of its people's hours; read only. */
  fixedCharges?: { values: Record<string, number>; label: string };
  /** Held back off each sprint after its adjustment, and paid back into the sprint it is released in. */
  retention?: {
    label: string;
    releaseLabel: string;
    /** The percentage of the version the sprint is priced under; zero holds nothing back. */
    percentFor: (sprintId: string) => number;
    /** What was released into each sprint, fixed when it was. */
    released: Record<string, number>;
    /** Closed as read; empty until the read answers, so nothing counts as held before it does. */
    closedSprintIds: string[];
    /** Absent for a reader who may not release it; a zero undoes that sprint's release. */
    onRelease?: (sprintId: string, amount: number) => void;
  };
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  // A swipe off the end of the year drags the grid and springs back, rather than going back a page.
  useEdgeBounce(scroller);
  /** Sparse: a cell absent here reads what is stored, which is what tells changed from untouched. */
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});
  const [roster, setRoster] = useState<FractionalDraft[] | null>(null);
  // A cell is a figure until it is asked for, so a stray scroll cannot nudge a number.
  const [active, setActive] = useState<{ sprintId: string; personId: string } | null>(null);
  /** What has been typed into the discount row, by sprint. Sparse, like the hours draft. */
  const [discountDraft, setDiscountDraft] = useState<Record<string, string>>({});
  /** The sprint the release dialog is open on, or null while it is shut. */
  const [releasing, setReleasing] = useState<string | null>(null);
  /** The reasons as typed, by sprint. Sparse, like the figures they explain. */
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  /** A way of looking at the grid rather than a fact about the product, so it is not remembered. */
  const [hideEmpty, setHideEmpty] = useState(false);
  // Off until asked for: the people alone are the plainer read.
  const [breakdown, setBreakdown] = useState(false);
  /** Read before the first paint: from an effect, every arrival replays a collapse nobody asked for. */
  const [folded, setFolded] = useState(() => readFolds(gridId));

  // Folds belong to the grid's own subject, so switching to another one reads its own.
  useEffect(() => setFolded(readFolds(gridId)), [gridId]);

  // A draft is a year's typing: paging to another year, or to another reading, is not a change to this one.
  const spanKey = `${reading ?? ''}:${sprints.map(sprint => sprint.id).join()}`;
  useEffect(() => {
    setDraft({});
    setRoster(null);
    setActive(null);
    setDiscountDraft({});
    setNoteDraft({});
    // The message is about the save that failed, and that save was another year's.
  }, [spanKey]);

  // The roster as typed, so a row just added has cells before it has a name.
  const rosterRows: FractionalDraft[] =
    roster ??
    (fractional?.people ?? []).map(person => ({
      id: person.id,
      name: person.name,
      rate: person.rate === null ? '' : String(person.rate),
    }));
  const fractionalGroup: GridGroup | null = fractional
    ? {
        key: fractional.key,
        label: fractional.label,
        people: rosterRows.map(row => ({ id: row.id, name: row.name })),
      }
    : null;
  // Last, under the chapters: they are part of the workforce, not a card of their own.
  const allGroups = fractionalGroup ? [...groups, fractionalGroup] : groups;
  const people = allGroups.flatMap(group => group.people);
  // Offered only where it says something: one chapter's breakdown is the total row again.
  const canBreakDown = allGroups.length > 1;
  const showsBreakdown = grouped || (breakdown && canBreakDown);
  const memberPeople = groups.flatMap(group => group.people);
  const inactivePeople = activeIds ? memberPeople.filter(person => !activeIds.has(person.id)) : [];
  // Only with people on both sides: with nobody active, folding everyone into Others would hide the whole team.
  const splitsByActivity = !showsBreakdown && inactivePeople.length > 0 && inactivePeople.length < memberPeople.length;
  /** The blocks as drawn: chapters, or the active people and then the rest, with the fractional rows last either way. */
  const shownGroups: GridGroup[] = splitsByActivity
    ? [
        {
          key: ACTIVE_GROUP,
          label: t('budget.activePeople'),
          people: memberPeople.filter(person => activeIds?.has(person.id)),
        },
        { key: INACTIVE_GROUP, label: t('budget.inactivePeople'), people: inactivePeople },
        ...(fractionalGroup ? [fractionalGroup] : []),
      ].filter(group => group.people.length > 0 || group === fractionalGroup)
    : allGroups;
  const showsHeads = showsBreakdown || splitsByActivity;
  /** Whether a block is folded away; the people no longer on the product and the fractional rows start folded. */
  const isShut = (group: GridGroup): boolean =>
    isFolded(folded, group.key, grouped || group.key === INACTIVE_GROUP || group.key === fractional?.key);
  const fold = (key: string, shut: boolean): void =>
    setFolded(current => {
      const next = setFold(current, key, shut);
      rememberFolds(gridId, next);
      return next;
    });
  const showsDiscount = !!discounts;
  // Only where a version holds something back or something was released: at 0% the row says nothing.
  const showsRetention =
    !!retention &&
    (sprints.some(sprint => retention.percentFor(sprint.id) > 0) || Object.keys(retention.released).length > 0);
  /** A group whose rows can be typed into in every sprint: on the forecast that is the fractional one alone. */
  const canType = (group: GridGroup): boolean =>
    !!onSave && (editableRows === 'all' || (!!fractional && group.key === fractional.key));
  /** One cell: its row's group allows it, or its sprint is one every row is typed into. */
  const typeableAt = (group: GridGroup, sprintId: string): boolean =>
    canType(group) || (!!onSave && !!typeableSprintIds?.includes(sprintId));
  const isForecast = !!projectedSprintIds;
  const isProjected = (sprintId: string): boolean => !!projectedSprintIds?.includes(sprintId);
  /** A forecast column's wash, green for spent and amber for projected; stronger on the head and total rows. */
  const columnTint = (sprintId: string, strong = false): string | false => {
    if (!isForecast) return false;
    if (isProjected(sprintId)) return strong ? projectedTintStrong : projectedTint;
    return strong ? actualTintStrong : actualTint;
  };
  /** A projection is a plan, so it is set in italics like the projections view's own figures. */
  const projectedInk = (sprintId: string): string | false => isProjected(sprintId) && 'italic';
  const groupOf = (personId: string): GridGroup | undefined =>
    allGroups.find(group => group.people.some(person => person.id === personId));
  const leadsMoney = lead === 'euros';
  const priced = !!rateFor || !!costs;

  /** What has been typed into a cell, or nothing where it has not been asked for. */
  const typedAt = (sprintId: string, personId: string): string | undefined => draft[sprintId]?.[personId];
  /** What the cell held when it was opened, so Escape undoes this edit rather than every earlier one. */
  const openedWith = useRef<{ had: boolean; value: string } | null>(null);
  const revertCell = (sprintId: string, personId: string): void =>
    setDraft(current => {
      const cells = { ...(current[sprintId] ?? {}) };
      const opened = openedWith.current;
      if (opened?.had) cells[personId] = opened.value;
      else delete cells[personId];
      return { ...current, [sprintId]: cells };
    });
  const typed = (sprintId: string, personId: string): string => typedAt(sprintId, personId) ?? '';
  // `asAmount` reads an empty string as zero, so a blank is answered here, not stored.
  const entered = (sprintId: string, personId: string): number | null => {
    const value = typed(sprintId, personId);
    return value.trim() === '' ? null : asAmount(value);
  };
  const invalid = (sprintId: string, personId: string): boolean =>
    typed(sprintId, personId).trim() !== '' && entered(sprintId, personId) === null;
  const stored = (sprintId: string, personId: string): number | null => hours[sprintId]?.[personId] ?? null;
  /** A cell holding something other than what is stored: what Save would write, and what is tinted. */
  const changed = (sprintId: string, personId: string): boolean =>
    typedAt(sprintId, personId) !== undefined &&
    !invalid(sprintId, personId) &&
    entered(sprintId, personId) !== stored(sprintId, personId);
  /** A sprint with nothing in it, as stored: nobody's hours, and nothing off the bill. */
  const isEmpty = (sprintId: string): boolean =>
    !people.some(person => hours[sprintId]?.[person.id] || costs?.[sprintId]?.[person.id]) &&
    !(showsDiscount && discounts?.values[sprintId]) &&
    // A fixed price bills its share whatever was logged; the €0 past its last sprint bills nothing.
    !fixedCharges?.values[sprintId] &&
    !retention?.released[sprintId];
  /** A sprint being filled in: the draft alone counts a cell merely opened, which seeds it as it stands. */
  const isTypedInto = (sprintId: string): boolean => {
    if (active?.sprintId === sprintId) return true;
    const rows = [...people, ...(roster ?? fractional?.people ?? [])];
    if (rows.some(row => changed(sprintId, row.id) || invalid(sprintId, row.id))) return true;
    const off = discountDraft[sprintId];
    return off !== undefined && off.trim() !== String(discounts?.values[sprintId]?.amount ?? '');
  };
  // Off what is stored, so typing into the last empty sprint does not take the control away.
  const empties = sprints.filter(sprint => isEmpty(sprint.id));
  const canHideEmpty = hidesEmptySprints && empties.length > 0 && empties.length < sprints.length;
  /** A column being typed into stays, so filling one in cannot take it away under the cursor. */
  const shown =
    hideEmpty && canHideEmpty ? sprints.filter(sprint => !isEmpty(sprint.id) || isTypedInto(sprint.id)) : sprints;
  // Every row declares the same columns, which is what lines them up.
  const columns = { gridTemplateColumns: hoursColumns(shown.length, totalColumn) };

  /** Where the arrival scroll left the box, so its own scroll event is not taken for the reader's. */
  const arrivedAt = useRef<number | null>(null);
  /** On arrival and a year change only: the grid must not move under somebody typing into it. */
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    // Every tab passes the product as gridId, so switching tabs keeps the sprint the reader was looking at.
    const kept = pointInView(gridId);
    const opensOn = shown.find(sprint => sprint.id === askedFor) ?? openingSprint(shown, dayKey(new Date()));
    const to =
      (kept ? scrollToPoint(el, kept) : undefined) ??
      (opensOn ? scrollToPoint(el, { sprintId: opensOn.id, at: 0.5 }) : undefined);
    if (to === undefined) return;
    el.scrollLeft = to;
    // Read back, since the browser clamps it: a fallback to today must not overwrite the kept point.
    arrivedAt.current = el.scrollLeft;
    // Not on the toggle: re-anchoring on today then scrolls an unsaved edit in a far sprint off screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spanKey]);

  /** The name is lent for the length of the transition: left on, the grid sits out every other one. */
  const transitions = useRef(0);
  const transitionGrid = (update: () => void): void => {
    const box = scroller.current;
    if (box) box.style.viewTransitionName = 'workforce-grid';
    transitions.current += 1;
    const mine = transitions.current;
    runViewTransition('fade', update).finally(() => {
      // Only the last press takes the name back off, or a double press ends the first one's turn early.
      if (box && mine === transitions.current) box.style.viewTransitionName = '';
    });
  };

  // Clearing takes the person out of the sprint rather than budgeting them at nothing.
  const valueIn = (sprintId: string, personId: string): number | null =>
    typedAt(sprintId, personId) === undefined ? stored(sprintId, personId) : entered(sprintId, personId);
  // What the row holds, typed or saved: the roster is one year's, so the year's own columns are all of it.
  const hasHours = (personId: string): boolean => sprints.some(sprint => valueIn(sprint.id, personId) !== null);
  /** The half of `badRow` the name field owns, so it can mark itself as the rate field does. */
  const nameHolds = (personId: string): boolean => {
    const row = rosterRows.find(each => each.id === personId);
    return !!row && row.name.trim() === '' && hasHours(personId);
  };
  // A row with hours and no name holds Save: `resolveRoster` would drop it, hours and all.
  const badRow = (row: FractionalDraft): boolean => asRate(row.rate) === undefined || nameHolds(row.id);
  const anyInvalid =
    sprints.some(sprint => people.some(person => invalid(sprint.id, person.id))) || rosterRows.some(badRow);

  const resolved: SprintHours = Object.fromEntries(
    sprints.map(sprint => [
      sprint.id,
      Object.fromEntries(
        people
          .map(person => [person.id, valueIn(sprint.id, person.id)] as const)
          .filter(([, amount]) => amount !== null),
      ) as Record<string, number>,
    ]),
  );

  // Dirty against what is stored: 40 retyped as 40.0 is the same budget.
  const asStored = (grid: SprintHours): string =>
    JSON.stringify(
      // Positions kept: dropping the blanks made 8 h moved from one person to another serialise the same.
      sprints.map(sprint => people.map(person => grid[sprint.id]?.[person.id] ?? null)),
    );
  // A name corrected or a rate agreed is worth saving even where not one hour moved.
  const asRoster = (people: FractionalPerson[]): string =>
    JSON.stringify(people.map(person => [person.name.trim().toLowerCase(), person.rate]));
  const rosterDirty = asRoster(resolveRoster(rosterRows)) !== asRoster(fractional?.people ?? []);
  const dirty = asStored(resolved) !== asStored(hours) || rosterDirty;

  /** What came off this sprint, as stored, or nothing where nothing did. */
  const discountOf = (sprintId: string): Discount | null => discounts?.values[sprintId] ?? null;
  /** The discount as typed, or nothing where its cell has not been typed into. */
  const typedOff = (sprintId: string): string | undefined => discountDraft[sprintId];
  /** A blank cell is no discount rather than a discount of nothing, so it takes the line away. */
  const enteredOff = (sprintId: string): number | null => typedAdjustment(typedOff(sprintId) ?? '');
  /** What the cell holds: what is typed, or what came off already, or nothing. */
  const shownOff = (sprintId: string): string => {
    const typedValue = typedOff(sprintId);
    if (typedValue !== undefined) return typedValue;
    const held = discountOf(sprintId);
    return held === null ? '' : asTyped(held.amount);
  };
  /** Kept because what holds Save should not depend on what a browser happens to let through. */
  const offInvalid = (sprintId: string): boolean => {
    const value = typedOff(sprintId);
    return value !== undefined && value.replace(EXTRA_CHARGE, '').trim() !== '' && typedAdjustment(value) === null;
  };
  /** What this sprint has off it as the grid stands: what is typed, or what is stored. */
  const discountNow = (sprintId: string): number | null =>
    typedOff(sprintId) === undefined ? (discountOf(sprintId)?.amount ?? null) : enteredOff(sprintId);
  /** Signed on sight, since the column is read at a glance: `-€250.00` off, `+€250.00` on. */
  const adjustmentText = (amount: number | null): string => {
    if (amount === null) return '';
    return amount < 0 ? `${EXTRA_CHARGE}${formatEur(-amount)}` : `-${formatEur(amount)}`;
  };
  /** An adjustment as a spreadsheet reads it, signed as money on the bill: 250 off is -250. */
  const offAsCopied = (sprintId: string): string => {
    const amount = discountNow(sprintId);
    return amount === null || amount === 0 ? '' : String(-amount);
  };
  /** Back the other way, into what the cell is typed in; what is not a figure is kept to be marked. */
  const offFromCopy = (value: string): string => {
    const text = value.trim();
    const off = text.startsWith('-');
    const amount = text === '' ? null : asAmount(text.replace(/^[+-]/, ''));
    if (amount === null) return value;
    return off ? String(amount) : `${EXTRA_CHARGE}${amount}`;
  };
  /** The reason as it stands: what is typed into the note, or what was written against the figure. */
  const noteNow = (sprintId: string): string => noteDraft[sprintId] ?? discountOf(sprintId)?.note ?? '';
  /** A note with no figure to explain is not a discount, so a cleared amount takes its reason too. */
  const resolvedDiscounts: SprintDiscounts = Object.fromEntries(
    sprints
      .map(sprint => [sprint.id, discountNow(sprint.id)] as const)
      .filter((entry): entry is readonly [string, number] => entry[1] !== null)
      .map(([sprintId, amount]) => [sprintId, { amount, note: noteNow(sprintId).trim() }]),
  );
  const asDiscounts = (values: SprintDiscounts): string =>
    JSON.stringify(sprints.map(sprint => [values[sprint.id]?.amount ?? null, values[sprint.id]?.note ?? '']));
  const offDirty = !!discounts && asDiscounts(resolvedDiscounts) !== asDiscounts(discounts.values);
  const anyOffInvalid = sprints.some(sprint => offInvalid(sprint.id));

  // Leaves the failure line alone: one press saves hours and discounts, and this runs on the way past.
  const discardDiscounts = (): void => {
    setDiscountDraft({});
    setNoteDraft({});
  };

  const saveDiscounts = (): void => {
    if (!discounts) return;
    discounts.onSave?.(resolvedDiscounts);
    discardDiscounts();
  };

  const hoursOf = (sprintId: string, personId: string): number | null => valueIn(sprintId, personId);
  /** A roster row is priced off its cell, not off the store: the rate is being typed in this grid. */
  const rateOf = (sprintId: string, personId: string): number | null => {
    if (!rateFor) return null;
    const row = rosterRows.find(each => each.id === personId);
    return row ? (asRate(row.rate) ?? null) : rateFor(sprintId, personId);
  };
  // Priced per person: two people on one rate card can still be on different rates.
  const costOf = (sprintId: string, personId: string): number | null =>
    costs ? (costs[sprintId]?.[personId] ?? null) : personCost(rateOf(sprintId, personId), hoursOf(sprintId, personId));
  const totalFor = (sprintId: string, over: GridPerson[], of: (s: string, p: string) => number | null): number =>
    sumCosts(over.map(person => of(sprintId, person.id)));

  /** What the work on screen came to, before anything is taken off it: the whole grid, or one chapter. */
  const workHours = (sprintId: string): number => totalFor(sprintId, people, hoursOf);
  const workCost = (sprintId: string): number => totalFor(sprintId, people, costOf);
  /** Money only: the hours behind a discount are still the hours the team reported. */
  const netCost = (sprintId: string): number =>
    workCost(sprintId) + (fixedCharges?.values[sprintId] ?? 0) - (showsDiscount ? (discountNow(sprintId) ?? 0) : 0);
  const heldIn = (sprintId: string): number =>
    showsRetention && retention ? heldBack(retention.percentFor(sprintId), netCost(sprintId)) : 0;
  /** What was paid back into this sprint; a release is made in its dialog and saved at once. */
  const releasedNow = (sprintId: string): number => retention?.released[sprintId] ?? 0;
  // Whatever leads off this year or tab asks first, for either of the two things the grid holds.
  useReportUnsaved('workforce', dirty || offDirty);
  /** Money off, like the adjustment: what is held back, less what was paid back into this sprint. */
  const retentionNow = (sprintId: string): number =>
    showsRetention && retention ? Math.round((heldIn(sprintId) - releasedNow(sprintId)) * 100) / 100 : 0;
  const gridCost = (sprintId: string): number => netCost(sprintId) - retentionNow(sprintId);
  // Closed sprints only: an open one's bill has not gone out, so nothing has been held back off it yet.
  // Read from the closed list, not the open one, whose empty value also means "not read yet".
  const heldClosed = sumCosts(
    sprints.filter(sprint => retention?.closedSprintIds.includes(sprint.id)).map(sprint => heldIn(sprint.id)),
  );
  const cents = (amount: number): number => Math.round(amount * 100) / 100;
  // Every release of the year, including one into a sprint this reading leaves off the grid.
  const heldSoFar = cents(heldClosed - sumCosts(Object.values(retention?.released ?? {})));
  // The grid's sprints, less the projected ones where the total counts actuals only.
  const overYear = (of: (sprintId: string) => number | null): number =>
    cents(
      sumCosts(sprints.filter(sprint => !totalActualsOnly || !isProjected(sprint.id)).map(sprint => of(sprint.id))),
    );
  // A closed sprint's bill has gone out, so money is only ever released into an open one.
  const isOpenSprint = (sprintId: string): boolean => !openSprintIds || openSprintIds.includes(sprintId);
  const openSprints = sprints.filter(sprint => isOpenSprint(sprint.id));
  /** Open sprints before this one that will hold money back once closed: not in the release yet. */
  const pendingBefore = (sprintId: string): { numbers: number[]; amount: number } => {
    const at = sprints.findIndex(sprint => sprint.id === sprintId);
    const pending = sprints
      .slice(0, Math.max(at, 0))
      .filter(sprint => isOpenSprint(sprint.id) && heldIn(sprint.id) > 0);
    return {
      numbers: pending.map(sprint => sprint.number),
      amount: cents(sumCosts(pending.map(sprint => heldIn(sprint.id)))),
    };
  };
  // The dialog saves at once, and a save reloads the grid, which would drop what is typed elsewhere in it.
  const releaseBlocked = dirty || offDirty;
  const canRelease = !!retention?.onRelease;
  // The version's percentage in the row's name; a year that changes it partway names each one.
  const retentionPercents = [...new Set(sprints.map(sprint => retention?.percentFor(sprint.id) ?? 0))].filter(
    percent => percent > 0,
  );
  const retentionShare = retentionPercents.map(percent => `${percent}%`).join(', ');
  // Copied out as one cell, so the spreadsheet keeps the percentage the screen puts under the label.
  const retentionName = retentionShare ? `${retention?.label} (${retentionShare})` : (retention?.label ?? '');
  const chapterHours = (group: GridGroup, sprintId: string): number => totalFor(sprintId, group.people, hoursOf);
  const chapterCost = (group: GridGroup, sprintId: string): number => totalFor(sprintId, group.people, costOf);

  const hoursText = (amount: number | null): string => {
    if (amount === null || amount === 0) return dash;
    return t('budget.hoursValue', { hours: formatBudgetHours(amount) });
  };
  // Hours with no rate read as a dash under them: the hours are known and the cost is not.
  const costText = (amount: number | null): string => {
    if (amount === null || amount === 0) return dash;
    return formatEur(amount);
  };

  /** Blank in a cell that can be typed into: the blank is what says the cell is a field. */
  const figures = (
    hours: number | null,
    cost: number | null,
    options?: { fillable?: boolean; className?: string },
  ): JSX.Element | null => {
    // A discount on a sprint nobody worked is a real total, so an empty cell needs an empty cost too.
    const moneyOnly = priced && cost !== null && cost !== 0;
    const className = options?.className;
    if ((hours === null || hours === 0) && !moneyOnly) {
      if (options?.fillable) return null;
      return <Figures lead={dash} className={className} />;
    }
    // Nothing to price with, so what is stored is the whole of what the cell has to say.
    if (!priced) {
      const text = leadsMoney ? costText(hours) : hoursText(hours);
      return <Figures lead={text} className={className} />;
    }
    const [line, under] = leadsMoney ? [costText(cost), hoursText(hours)] : [hoursText(hours), costText(cost)];
    return <Figures lead={line} under={under} className={className} />;
  };

  /** Plainly: "€12,345.00" arrives in a spreadsheet cell as text, and text does not add up. */
  const readForCopy = (row: string, col: string, unit: CopyUnit): string => {
    const leads: 'hours' | 'cost' = leadsMoney ? 'cost' : 'hours';
    // Without a rate a cell holds one figure: pricing a grid of euros again gives nothing.
    const asked: 'hours' | 'cost' = unit === 'lead' ? leads : unit;
    const wants: 'hours' | 'cost' = priced ? asked : 'hours';
    const plain = (amount: number | null): string => (amount === null || amount === 0 ? '' : String(amount));

    if (col === TOTAL_COL) {
      if (row === HEAD_ROW) return t('budget.gridTotal');
      return plain(overYear(sprintId => Number(readForCopy(row, sprintId, unit)) || null));
    }
    if (row === HEAD_ROW) {
      if (col === NAME_COL) return nameLabel ?? t('member');
      const sprint = sprints.find(each => each.id === col);
      return sprint ? t('budget.sprintShort', { number: sprint.number }) : '';
    }
    if (row === TOTAL_ROW) {
      if (col === NAME_COL) return t('budget.gridTotal');
      return plain(wants === 'cost' ? gridCost(col) : workHours(col));
    }
    const chapter = shownGroups.find(group => chapterRow(group.key) === row);
    if (chapter) {
      if (col === NAME_COL) return chapter.label;
      return plain(wants === 'cost' ? chapterCost(chapter, col) : chapterHours(chapter, col));
    }
    if (row === FIXED_ROW) {
      if (col === NAME_COL) return fixedCharges?.label ?? '';
      return wants === 'cost' ? plain(fixedCharges?.values[col] ?? null) : '';
    }
    if (row === DISCOUNT_ROW) {
      if (col === NAME_COL) return discounts?.label ?? '';
      // Money off a bill, so it is money or it is nothing: there are no hours behind a discount.
      return wants === 'cost' ? plain(discountNow(col) === null ? null : -(discountNow(col) as number)) : '';
    }
    if (row === RETENTION_ROW) {
      if (col === NAME_COL) return retentionName;
      return wants === 'cost' ? plain(-heldIn(col)) : '';
    }
    if (row === RELEASE_ROW) {
      if (col === NAME_COL) return retention?.releaseLabel ?? '';
      return wants === 'cost' ? plain(releasedNow(col)) : '';
    }
    if (col === NAME_COL) return people.find(person => person.id === row)?.name ?? '';
    return plain(wants === 'cost' ? costOf(col, row) : hoursOf(col, row));
  };

  /** Whether the row that offers to name another line is on screen, since it is a row of the grid. */
  const hasAddRow = !!fractionalGroup && canType(fractionalGroup) && allGroups.includes(fractionalGroup);
  // Inside the chapter's fold, so folding takes the add row with it and a block cannot reach into it.
  const addRowShown = hasAddRow && !(showsHeads && isShut(fractionalGroup));
  /** The whole table as drawn; only a member's own cells can be written. */
  const range = useCellRange({
    rows: [
      HEAD_ROW,
      ...shownGroups.flatMap(group => [
        ...(showsHeads ? [chapterRow(group.key)] : []),
        // A block cannot reach across a folded chapter into cells nobody can see.
        ...(showsHeads && isShut(group) ? [] : group.people.map(person => person.id)),
      ]),
      ...(addRowShown ? [ADD_ROW] : []),
      ...(fixedCharges ? [FIXED_ROW] : []),
      ...(showsDiscount ? [DISCOUNT_ROW] : []),
      ...(showsRetention ? [RETENTION_ROW, RELEASE_ROW] : []),
      TOTAL_ROW,
    ],
    cols: [NAME_COL, ...shown.map(sprint => sprint.id), ...(totalColumn ? [TOTAL_COL] : [])],
    readCell: (row, col, unit) => readForCopy(row, col, unit),
    writeCells:
      onSave || discounts?.onSave
        ? cells => {
            const [offs, hoursCells] = [
              cells.filter(cell => cell.row === DISCOUNT_ROW),
              cells.filter(cell => cell.row !== DISCOUNT_ROW),
            ];
            if (hoursCells.length)
              setDraft(current => {
                const next = { ...current };
                hoursCells.forEach(({ row: personId, col: sprintId, value }) => {
                  next[sprintId] = { ...next[sprintId], [personId]: value };
                });
                return next;
              });
            if (offs.length)
              setDiscountDraft(current => {
                const next = { ...current };
                offs.forEach(({ col: sprintId, value }) => {
                  next[sprintId] = offFromCopy(value);
                });
                return next;
              });
          }
        : undefined,
    // A member's own cell in a typeable block, or an adjustment: the rest is worked out for you.
    isEditable: (personId, sprintId) => {
      if (sprintId === NAME_COL || sprintId === TOTAL_COL) return false;
      if (personId === DISCOUNT_ROW) return !!discounts?.onSave;
      const group = groupOf(personId);
      return !!group && typeableAt(group, sprintId);
    },
    passiveRows: [ADD_ROW],
    resetOn: `${gridId}:${spanKey}`,
    leadIsCost: leadsMoney,
    // With a rate, a cell carries two figures, and which one a copy takes is the reader's to say.
    twoFigures: priced,
    // Hours are what a member's cell is typed in; an adjustment is money, as it copies.
    readForFill: (row, col) => (row === DISCOUNT_ROW ? offAsCopied(col) : readForCopy(row, col, 'hours')),
    // Hours are not euros, so a fill never carries one between a member's row and the adjustment.
    fillsFrom: (from, to) => (from.row === DISCOUNT_ROW) === (to.row === DISCOUNT_ROW),
  });

  /** Opens holding what it reads, so typing replaces the figure rather than appending to it. */
  const open = (sprintId: string, personId: string): void => {
    // The cell being typed into is also where a pasted block would land.
    range.anchorAt(personId, sprintId);
    const prior = draft[sprintId]?.[personId];
    openedWith.current = { had: prior !== undefined, value: prior ?? '' };
    setDraft(current => {
      if (current[sprintId]?.[personId] !== undefined) return current;
      const held = stored(sprintId, personId);
      return {
        ...current,
        [sprintId]: { ...current[sprintId], [personId]: held === null ? '' : formatBudgetHours(held) },
      };
    });
    setActive({ sprintId, personId });
  };
  const isActive = (sprintId: string, personId: string): boolean =>
    active?.sprintId === sprintId && active?.personId === personId;

  const typeRow = (id: string, field: 'name' | 'rate', value: string): void =>
    setRoster(current => (current ?? rosterRows).map(row => (row.id === id ? { ...row, [field]: value } : row)));
  const addRow = (): void =>
    setRoster(current => [...(current ?? rosterRows), { id: newFractionalId(), name: '', rate: '' }]);
  const removeRow = (id: string): void => {
    setRoster(current => (current ?? rosterRows).filter(row => row.id !== id));
    // Its hours go with it, so a row put back does not come back filled in.
    setDraft(current =>
      Object.fromEntries(
        Object.entries(current).map(([sprintId, cells]) => [
          sprintId,
          Object.fromEntries(Object.entries(cells).filter(([personId]) => personId !== id)),
        ]),
      ),
    );
  };

  const discard = (): void => {
    setDraft({});
    setRoster(null);
    setActive(null);
    range.clear();
  };

  const save = (): void => {
    if (!onSave) return;
    onSave(resolved, resolveRoster(rosterRows));
    discard();
  };

  /** What a cell needs to be part of the block: the wash where it is in one, and the gestures. */
  const inBlock = (row: string, col: string, options?: { hatched?: boolean }) => ({
    className: range.selected(row, col) ? rangeOverlay(range.edges(row, col), options) : undefined,
    ...range.cellHandlers(row, col),
  });

  const totalCell = (
    row: string,
    content: React.ReactNode,
    className = hoursEndCell,
    options: { hatched?: boolean } = {},
  ): JSX.Element | null =>
    totalColumn ? (
      <div
        role={row === HEAD_ROW ? 'columnheader' : 'cell'}
        data-total-column
        {...inBlock(row, TOTAL_COL, options)}
        className={cx(className, inBlock(row, TOTAL_COL, options).className)}
      >
        {content}
      </div>
    ) : null;

  /** One person of a chapter, drawn the same whether or not the breakdown groups them. */
  const personRow = (person: GridPerson, group: GridGroup): JSX.Element => {
    const isFractional = !!fractional && group.key === fractional.key;
    // Hovering the row tints every cell it can be typed into, so the reach is one movement.
    return (
      <div key={person.id} role="row" style={columns} className={cx(hoursRow, 'group/row')}>
        {isFractional && canType(group) ? (
          <div
            role="rowheader"
            {...inBlock(person.id, NAME_COL)}
            className={cx(hoursNameFlush, 'font-normal', inBlock(person.id, NAME_COL).className)}
          >
            {/* Named and priced here: there is no member record to read either from. */}
            <div className="flex size-full items-center gap-1 pl-3 pr-1">
              <Input
                aria-label={t('budget.fractionalNameOf', {
                  number: group.people.indexOf(person) + 1,
                })}
                value={rosterRows.find(row => row.id === person.id)?.name ?? ''}
                placeholder={t('budget.fractionalName')}
                autoFocus={person.name === ''}
                onChange={event => typeRow(person.id, 'name', event.target.value)}
                className={cx(
                  'h-8 min-w-0 flex-1 bg-popover',
                  // Its own half of `badRow`: a bad rate is the rate field's to mark, not this one's.
                  nameHolds(person.id) && 'border-destructive bg-destructive/10',
                )}
              />
              <Input
                type="number"
                min={0}
                step="0.01"
                {...moneyField}
                aria-label={t('budget.fractionalRateOf', {
                  name: person.name || t('budget.fractionalName'),
                })}
                value={rosterRows.find(row => row.id === person.id)?.rate ?? ''}
                placeholder={t('budget.fractionalRate')}
                onChange={event => typeRow(person.id, 'rate', event.target.value)}
                className={cx(
                  'h-8 w-16 bg-popover text-right',
                  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                  asRate(rosterRows.find(row => row.id === person.id)?.rate ?? '') === undefined &&
                    'border-destructive bg-destructive/10',
                )}
              />
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={t('budget.removeFractional', {
                  name: person.name || t('budget.fractionalName'),
                })}
                onClick={() => removeRow(person.id)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <div
            role="rowheader"
            {...inBlock(person.id, NAME_COL)}
            className={cx(hoursNameCell, 'font-normal', inBlock(person.id, NAME_COL).className)}
          >
            <span className="truncate">{person.name}</span>
            {/* A fractional row carries its own price, so it is said on the row. */}
            {isFractional && (
              <span className="ml-2 shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                {rateLabel(fractional?.people.find(each => each.id === person.id)?.rate ?? null)}
              </span>
            )}
          </div>
        )}
        {shown.map(sprint => {
          const cellLabel = `${person.name}, ${t('budget.sprintNumber', { number: sprint.number })}`;
          const mine = typeableAt(group, sprint.id);
          const typing = mine && isActive(sprint.id, person.id);
          return (
            <div
              key={sprint.id}
              role="cell"
              // Tab lands on the cell and landing opens it, so the grid still walks by keyboard.
              tabIndex={mine && !typing ? 0 : undefined}
              onFocus={mine && !typing ? () => open(sprint.id, person.id) : undefined}
              // A drag or shift-click has already claimed the press for the block.
              onClick={mine && !typing ? () => !range.claimedClick() && open(sprint.id, person.id) : undefined}
              // Read-only cells join a block too: a grid nobody types into is still copied out of.
              {...(typing ? range.openCellHandlers(person.id, sprint.id) : range.cellHandlers(person.id, sprint.id))}
              onKeyDown={
                mine && !typing
                  ? event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      // Space scrolls the page otherwise, and this grid is a page of rows.
                      event.preventDefault();
                      open(sprint.id, person.id);
                    }
                  : undefined
              }
              className={cx(
                typing ? hoursCellFlush : hoursCell,
                !typing && !invalid(sprint.id, person.id) && columnTint(sprint.id),
                mine &&
                  !typing &&
                  cx(
                    'cursor-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                    hoverDarkenRow,
                    hoverDarken,
                  ),
                // An outline, not a tint: it has to read over the grid's own tints, and mark an emptied cell.
                !typing && changed(sprint.id, person.id) && 'ring-1 ring-inset ring-foreground/30',
                !typing && invalid(sprint.id, person.id) && 'bg-destructive/10',
                !typing && range.selected(person.id, sprint.id) && rangeOverlay(range.edges(person.id, sprint.id)),
                typing &&
                  range.selected(person.id, sprint.id) &&
                  rangeOverlay(range.edges(person.id, sprint.id), { open: true }),
              )}
            >
              {typing ? (
                <Input
                  // A number field empties what it cannot parse, and a paste can put "7,5" here.
                  {...(numberFieldHolds(typed(sprint.id, person.id))
                    ? { type: 'number', min: 0, step: '0.5' }
                    : { type: 'text', inputMode: 'decimal' as const })}
                  aria-label={cellLabel}
                  placeholder={t('budget.hoursPlaceholder')}
                  value={typed(sprint.id, person.id)}
                  onChange={e =>
                    setDraft(current => ({
                      ...current,
                      [sprint.id]: { ...current[sprint.id], [person.id]: e.target.value },
                    }))
                  }
                  autoFocus
                  onBlur={() => setActive(null)}
                  // The container's keys are off while a cell is open, so the way out is here.
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setActive(null);
                      return;
                    }
                    if (event.key !== 'Escape') return;
                    event.preventDefault();
                    revertCell(sprint.id, person.id);
                    setActive(null);
                  }}
                  // A number field takes the wheel as input, and this grid is scrolled past it.
                  onWheel={e => e.currentTarget.blur()}
                  className={cx(
                    'size-full rounded-none border-0 px-3 text-right',
                    // Inside its own cell: the kit rings 3px outwards, over the rows either side.
                    'focus-visible:ring-2 focus-visible:ring-inset',
                    '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                    invalid(sprint.id, person.id)
                      ? 'bg-destructive/10 hover:bg-destructive/15'
                      : 'bg-muted/20 hover:bg-muted/40',
                  )}
                />
              ) : (
                figures(hoursOf(sprint.id, person.id), costOf(sprint.id, person.id), {
                  fillable: mine,
                  className: cx(
                    changed(sprint.id, person.id) && 'font-medium text-foreground',
                    // Not on a gap: two text colours are settled by stylesheet order, and grey won over red.
                    !changed(sprint.id, person.id) && 'text-muted-foreground',
                    projectedInk(sprint.id),
                  ),
                })
              )}
            </div>
          );
        })}
        {totalCell(
          person.id,
          figures(
            overYear(sprintId => hoursOf(sprintId, person.id)),
            overYear(sprintId => costOf(sprintId, person.id)),
            { className: 'font-bold text-foreground' },
          ),
        )}
      </div>
    );
  };

  return (
    // `overflow-clip`, not `hidden`: hidden is a scroll container, and the name column sticks to one.
    <section className={cx('overflow-clip', cardShellClass)}>
      <div className={cx(cardHeaderClass, 'print:hidden')}>
        <div className={cardHeaderTitleClass}>
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {hint && (
            <p key="hint" className={cx(hintText, hintFade)}>
              {hint}
            </p>
          )}
        </div>
        {/* A fixed height whether or not it holds a control, or the grid steps down between tabs. */}
        <div className="flex min-h-7 shrink-0 items-center gap-2">
          {controls}
          {canBreakDown && !grouped && (
            <label
              // base-ui names a checkbox by the label whose id is the control's plus -label.
              id={`${gridId}-breakdown-label`}
              htmlFor={`${gridId}-breakdown`}
              className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"
            >
              <Checkbox
                id={`${gridId}-breakdown`}
                checked={breakdown}
                onCheckedChange={next => transitionGrid(() => setBreakdown(next === true))}
              />
              {t('budget.chapterBreakdown')}
            </label>
          )}
          {/* Offered only where there are empty sprints to hide and something left when they go. */}
          {canHideEmpty && (
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={hideEmpty}
              onClick={() => transitionGrid(() => setHideEmpty(!hideEmpty))}
            >
              {/* A calendar, not an eye: what is hidden is stretches of time, and the eye is Client view. */}
              {hideEmpty ? <Calendar className="size-3.5" /> : <CalendarOff className="size-3.5" />}
              {hideEmpty ? t('budget.showAllSprints') : t('budget.hideEmptySprints')}
            </Button>
          )}
          {cornerControls}
          {/* Held, not hidden, while a cell is invalid: a red cell with nothing to press is a dead end. */}
          {/* Typing that is not a figure is still something to press about: it cannot be saved. */}
          {(dirty || offDirty || anyInvalid || anyOffInvalid) && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  discard();
                  discardDiscounts();
                }}
              >
                {t('budget.discard')}
              </Button>
              <Button
                size="sm"
                disabled={anyInvalid || anyOffInvalid}
                onClick={() => {
                  if (dirty) save();
                  if (offDirty) saveDiscounts();
                }}
              >
                {t('actions.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      {!people.length && !fractional ? (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">{emptyNote ?? t('budget.noPeople')}</p>
      ) : (
        <div
          ref={scroller}
          onScroll={event => {
            if (event.currentTarget.scrollLeft === arrivedAt.current) return;
            arrivedAt.current = null;
            const point = pointAtCentre(event.currentTarget);
            if (point) rememberPointInView(gridId, point);
          }}
          // The table's own width, up to the card's: two columns in a card-wide box stop mid-card.
          className={cx(
            'no-scrollbar mx-5 w-fit max-w-[calc(100%-2.5rem)] overflow-x-auto rounded-xl border border-border',
            'max-md:mx-4 max-md:max-w-[calc(100%-2rem)]',
            'mb-5',
          )}
        >
          {/* The browser's own bar is hidden: the rail at the foot of this box is the one that shows. */}
          {/* The roles put the table back for anything reading this rather than looking at it. */}
          {/* The block's own box: the keys and the clipboard are its, but only while no cell is open. */}
          <div
            role="table"
            aria-label={title}
            ref={range.containerHandlers.ref}
            tabIndex={range.containerHandlers.tabIndex}
            onCopy={active ? undefined : range.containerHandlers.onCopy}
            // A block closes the cell it landed on, so it is read as figures, not through the field.
            onPaste={event => {
              range.containerHandlers.onPaste(event);
              if (event.defaultPrevented) setActive(null);
            }}
            onKeyDown={active ? undefined : range.containerHandlers.onKeyDown}
            style={twoFigureRow}
            className={`min-w-max ${rangeContainer}`}
          >
            {/* Taller on a forecast, for the line under the dates that says which figures the column holds. */}
            <div role="row" style={isForecast ? { ...columns, ...forecastHeadRow } : columns} className={hoursRow}>
              <div
                role="columnheader"
                {...inBlock(HEAD_ROW, NAME_COL)}
                className={cx(hoursNameCell, 'font-medium', inBlock(HEAD_ROW, NAME_COL).className)}
              >
                {nameLabel ?? t('member')}
              </div>
              {shown.map(sprint => (
                <div
                  key={sprint.id}
                  role="columnheader"
                  // Which sprint this column is, for the arrival scroll to find it by.
                  data-sprint={sprint.id}
                  title={t('budget.sprintNumber', { number: sprint.number })}
                  {...inBlock(HEAD_ROW, sprint.id)}
                  className={cx(
                    hoursHeadCell,
                    columnTint(sprint.id, true) || gridHeadTint,
                    inBlock(HEAD_ROW, sprint.id).className,
                  )}
                >
                  <span className="flex flex-col items-end">
                    <Figures
                      // The dates are the widest line a phone's narrow column holds.
                      className="max-md:[&>span+span]:text-[0.625rem]"
                      lead={t('budget.sprintShort', { number: sprint.number })}
                      under={
                        sprint.startDate && sprint.endDate
                          ? formatSprintRange(new Date(sprint.startDate), new Date(sprint.endDate), i18n?.language)
                          : undefined
                      }
                    />
                    {isForecast && (
                      <span className="text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(isProjected(sprint.id) ? 'forecast.projected' : 'forecast.actual')}
                      </span>
                    )}
                  </span>
                </div>
              ))}
              {totalCell(
                HEAD_ROW,
                totalNote ? (
                  <span className="flex flex-col items-end">
                    {t('budget.gridTotal')}
                    <span className="text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground">
                      {totalNote}
                    </span>
                  </span>
                ) : (
                  t('budget.gridTotal')
                ),
                cx(hoursEndCell, 'font-bold'),
              )}
            </div>

            {shownGroups.map(group => {
              // Only a drawn heading folds its block; with none, everyone is on screen.
              const shut = showsHeads && isShut(group);
              return (
                <div key={group.key || 'unfiled'} role="rowgroup">
                  {/* The chapter's own line, in its own box so ticking the breakdown glides it in. */}
                  <div
                    role="rowgroup"
                    style={{ '--rows': 1 } as React.CSSProperties}
                    className={cx(hoursFoldBox, showsHeads ? hoursFoldOpen : hoursFoldShut)}
                  >
                    <div role="row" style={columns} className={hoursRow}>
                      <div
                        role="rowheader"
                        {...inBlock(chapterRow(group.key), NAME_COL)}
                        className={cx(
                          hoursNameFlush,
                          'font-semibold',
                          inBlock(chapterRow(group.key), NAME_COL).className,
                        )}
                      >
                        <button
                          type="button"
                          // A drag out of the chapter's name is a block, not an ask to fold it away.
                          onClick={() => !range.claimedClick() && fold(group.key, !shut)}
                          aria-expanded={!shut}
                          tabIndex={showsHeads ? undefined : -1}
                          className="flex size-full cursor-pointer items-center gap-1.5 px-3 text-left font-semibold text-foreground"
                        >
                          <ChevronDown
                            className={cx(
                              'size-4 shrink-0 text-muted-foreground transition-transform duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
                              shut && '-rotate-90',
                            )}
                          />
                          <span className="truncate" title={group.label}>
                            {group.label}
                          </span>
                          {/* Kept on the page and faded, so folding eases the count in rather than popping it. */}
                          {group.people.length > 0 && (
                            <span
                              data-fold-count
                              aria-hidden={!shut}
                              className={cx(
                                'shrink-0 rounded-full bg-muted px-1.5 text-xs font-normal tabular-nums text-muted-foreground',
                                'transition-opacity duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
                                shut ? 'opacity-100' : 'opacity-0',
                              )}
                            >
                              {group.people.length}
                            </span>
                          )}
                        </button>
                      </div>
                      {shown.map(sprint => (
                        <div
                          key={sprint.id}
                          role="cell"
                          {...inBlock(chapterRow(group.key), sprint.id)}
                          className={cx(
                            hoursCell,
                            'font-semibold',
                            columnTint(sprint.id, true) || gridHeadTint,
                            inBlock(chapterRow(group.key), sprint.id).className,
                          )}
                        >
                          {figures(chapterHours(group, sprint.id), chapterCost(group, sprint.id), {
                            className: projectedInk(sprint.id) || undefined,
                          })}
                        </div>
                      ))}
                      {totalCell(
                        chapterRow(group.key),
                        figures(
                          overYear(sprintId => chapterHours(group, sprintId)),
                          overYear(sprintId => chapterCost(group, sprintId)),
                        ),
                        cx(hoursEndCell, 'font-semibold'),
                      )}
                    </div>
                  </div>

                  {/* Clipped, so folding cuts the block off the bottom and leaves its rows their own height. */}
                  <div
                    role="rowgroup"
                    style={
                      {
                        '--rows': group.people.length + (hasAddRow && group === fractionalGroup ? 1 : 0),
                      } as React.CSSProperties
                    }
                    className={cx(hoursFoldBox, shut ? hoursFoldShut : hoursFoldOpen)}
                  >
                    {group.people.map(person => personRow(person, groupOf(person.id) ?? group))}
                    {/* The last row of the workforce, under everybody: another bought-in line to name. */}
                    {hasAddRow && group === fractionalGroup && (
                      <div role="row" style={columns} className={hoursRow}>
                        <div
                          role="rowheader"
                          {...inBlock(ADD_ROW, NAME_COL, { hatched: true })}
                          className={cx(
                            hoursNameFlush,
                            'font-normal',
                            inBlock(ADD_ROW, NAME_COL, { hatched: true }).className,
                          )}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="relative z-10 ml-2"
                            // A drag out of this row is a block, not an ask to add a line.
                            onClick={() => !range.claimedClick() && addRow()}
                          >
                            <Plus className="size-4" />
                            {t('budget.addFractional')}
                          </Button>
                        </div>
                        {shown.map(sprint => (
                          <div
                            key={sprint.id}
                            role="cell"
                            {...inBlock(ADD_ROW, sprint.id, { hatched: true })}
                            className={cx(
                              hoursCell,
                              columnTint(sprint.id),
                              inBlock(ADD_ROW, sprint.id, { hatched: true }).className,
                            )}
                          />
                        ))}
                        {totalCell(ADD_ROW, null, hoursEndCell, { hatched: true })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {fixedCharges && (
              <div role="row" style={columns} className={hoursRow}>
                <div
                  role="rowheader"
                  {...inBlock(FIXED_ROW, NAME_COL)}
                  className={cx(hoursNameCell, 'font-bold', inBlock(FIXED_ROW, NAME_COL).className)}
                >
                  {fixedCharges.label}
                </div>
                {shown.map(sprint => (
                  <div
                    key={sprint.id}
                    role="cell"
                    {...inBlock(FIXED_ROW, sprint.id)}
                    className={cx(hoursCell, columnTint(sprint.id), inBlock(FIXED_ROW, sprint.id).className)}
                  >
                    <span className="whitespace-nowrap text-sm tabular-nums text-foreground">
                      {fixedCharges.values[sprint.id] === undefined ? '' : formatEur(fixedCharges.values[sprint.id])}
                    </span>
                  </div>
                ))}
                {totalCell(
                  FIXED_ROW,
                  <span className="whitespace-nowrap text-sm tabular-nums text-foreground">
                    {formatEur(overYear(sprintId => fixedCharges.values[sprintId] ?? null))}
                  </span>,
                )}
              </div>
            )}

            {/* What came off the bill, as its own line rather than written over someone's hours. */}
            {showsDiscount && discounts && (
              <div role="row" style={columns} className={cx(hoursRow, 'group/row')}>
                <div
                  role="rowheader"
                  {...inBlock(DISCOUNT_ROW, NAME_COL)}
                  className={cx(hoursNameCell, 'font-bold', inBlock(DISCOUNT_ROW, NAME_COL).className)}
                >
                  {discounts.label}
                </div>
                {shown.map(sprint => {
                  const typing = active?.sprintId === sprint.id && active?.personId === DISCOUNT_ROW;
                  const typeable = !!discounts.onSave;
                  const open = (): void => {
                    // What the cell held on the way in, so Escape undoes this edit and not an earlier one.
                    const prior = discountDraft[sprint.id];
                    openedWith.current = { had: prior !== undefined, value: prior ?? '' };
                    // The cell being typed into is the block, so it is outlined and can be filled onwards.
                    range.anchorAt(DISCOUNT_ROW, sprint.id);
                    setActive({ sprintId: sprint.id, personId: DISCOUNT_ROW });
                  };
                  return (
                    <div
                      key={sprint.id}
                      role="cell"
                      // Landing opens it, unless what landed belongs to the note (see `ownEvent`).
                      tabIndex={typing || !typeable ? undefined : 0}
                      onFocus={typing || !typeable ? undefined : event => ownEvent(event) && open()}
                      // A drag or a shift-click has already claimed the press for the block.
                      onClick={
                        typing || !typeable ? undefined : event => ownEvent(event) && !range.claimedClick() && open()
                      }
                      {...(typing ? range.openCellHandlers(DISCOUNT_ROW, sprint.id) : inBlock(DISCOUNT_ROW, sprint.id))}
                      onKeyDown={
                        typing || !typeable
                          ? undefined
                          : event => {
                              if (!ownEvent(event) || (event.key !== 'Enter' && event.key !== ' ')) return;
                              // Space scrolls the page otherwise, and this grid is a page of rows.
                              event.preventDefault();
                              open();
                            }
                      }
                      className={cx(
                        typing ? hoursCellFlush : cx(hoursCell, 'group/cell gap-1.5'),
                        // An adjustment is money already off a bill, so it reads as spent whichever column it is in.
                        !typing &&
                          !offInvalid(sprint.id) &&
                          (isForecast && discountNow(sprint.id) !== null ? actualTint : columnTint(sprint.id)),
                        !typing &&
                          typeable &&
                          cx(
                            'cursor-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                            hoverDarkenRow,
                            hoverDarken,
                          ),
                        // An outline, not a tint: an unclosed sprint is already tinted.
                        !typing && offInvalid(sprint.id) && 'ring-1 ring-inset ring-destructive',
                        // The cell carries the fill, not the field: the minus sits beside the field.
                        typing && (offInvalid(sprint.id) ? 'bg-destructive/10' : 'bg-muted/20'),
                        !typing && inBlock(DISCOUNT_ROW, sprint.id).className,
                        typing &&
                          range.selected(DISCOUNT_ROW, sprint.id) &&
                          rangeOverlay(range.edges(DISCOUNT_ROW, sprint.id), { open: true }),
                      )}
                    >
                      {typing ? (
                        <>
                          {/* Signed while typing, so nobody has to work out which way the figure goes. */}
                          {shownOff(sprint.id).trim() !== '' && (
                            <span className="text-sm tabular-nums text-muted-foreground">
                              {shownOff(sprint.id).startsWith(EXTRA_CHARGE) ? t('spend.more') : t('spend.less')}
                            </span>
                          )}
                          <Input
                            // Text, not number: a number field reports `+5` as empty.
                            type="text"
                            inputMode="decimal"
                            aria-label={`${discounts.label}, ${t('budget.sprintNumber', { number: sprint.number })}`}
                            value={shownOff(sprint.id).replace(EXTRA_CHARGE, '')}
                            autoFocus
                            onBlur={() => setActive(null)}
                            // The container's keys are off while a cell is open, so the way out is here.
                            onKeyDown={event => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                setActive(null);
                                return;
                              }
                              if (event.key !== 'Escape') return;
                              event.preventDefault();
                              setDiscountDraft(current => {
                                const next = { ...current };
                                const opened = openedWith.current;
                                if (opened?.had) next[sprint.id] = opened.value;
                                else delete next[sprint.id];
                                return next;
                              });
                              setActive(null);
                            }}
                            onChange={event => {
                              const typed = event.target.value;
                              const figure = typed.replace(/[+-]/g, '');
                              // The field shows no sign, so a typed one is a fresh choice; none keeps the last.
                              const adds = typed.includes('-')
                                ? false
                                : typed.includes(EXTRA_CHARGE) || shownOff(sprint.id).startsWith(EXTRA_CHARGE);
                              setDiscountDraft(current => ({
                                ...current,
                                [sprint.id]: adds ? `${EXTRA_CHARGE}${figure}` : figure,
                              }));
                            }}
                            className={cx(
                              // Sized to what is typed, so the minus stays in front of the figure.
                              'h-full w-auto min-w-3 field-sizing-content rounded-none border-0 px-0 pr-3 text-right tabular-nums',
                              // The cell rings instead, around the minus and the figure together.
                              'focus-visible:ring-0',
                              // The cell's fill shows through: the kit's field carries `dark:bg-input/30`.
                              'bg-transparent dark:bg-transparent',
                            )}
                          />
                        </>
                      ) : (
                        <>
                          {/* Why, beside the figure: the half nobody remembers a month later. */}
                          {discountNow(sprint.id) !== null && (
                            <DiscountNote
                              label={t('spend.discountNoteOf', { number: sprint.number })}
                              title={t('budget.sprintNumber', { number: sprint.number })}
                              placeholder={t('spend.discountNotePlaceholder')}
                              note={noteNow(sprint.id)}
                              onChange={
                                typeable
                                  ? value => setNoteDraft(current => ({ ...current, [sprint.id]: value }))
                                  : undefined
                              }
                            />
                          )}
                          <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                            {adjustmentText(discountNow(sprint.id))}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
                {totalCell(
                  DISCOUNT_ROW,
                  <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                    {adjustmentText(overYear(discountNow) || null)}
                  </span>,
                )}
              </div>
            )}

            {showsRetention && retention && (
              <>
                <div role="row" style={columns} className={hoursRow}>
                  <div
                    role="rowheader"
                    {...inBlock(RETENTION_ROW, NAME_COL)}
                    className={cx(hoursNameCell, 'font-bold', inBlock(RETENTION_ROW, NAME_COL).className)}
                  >
                    {/* Under the label, as the Release row carries what is held. */}
                    <span className="flex flex-col leading-tight">
                      <span>{retention.label}</span>
                      {retentionShare ? (
                        <span className="text-xs font-normal text-muted-foreground">
                          {t('spend.retentionShare', { share: retentionShare })}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {shown.map(sprint => (
                    <div
                      key={sprint.id}
                      role="cell"
                      {...inBlock(RETENTION_ROW, sprint.id)}
                      className={cx(hoursCell, columnTint(sprint.id), inBlock(RETENTION_ROW, sprint.id).className)}
                    >
                      <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                        {adjustmentText(heldIn(sprint.id) || null)}
                      </span>
                    </div>
                  ))}
                  {totalCell(
                    RETENTION_ROW,
                    <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                      {adjustmentText(overYear(heldIn) || null)}
                    </span>,
                  )}
                </div>

                {/* What was paid back into each sprint's bill, released from what is held through its dialog. */}
                <div role="row" style={columns} className={hoursRow}>
                  <div
                    role="rowheader"
                    {...inBlock(RELEASE_ROW, NAME_COL)}
                    className={cx(hoursNameCell, 'font-bold', inBlock(RELEASE_ROW, NAME_COL).className)}
                  >
                    <span className="flex flex-col leading-tight">
                      <span>{retention.releaseLabel}</span>
                      <span className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                        {t('spend.retentionHeld', { amount: formatEur(Math.max(heldSoFar, 0)) })}
                      </span>
                    </span>
                  </div>
                  {shown.map(sprint => {
                    const offered = canRelease && isOpenSprint(sprint.id) && !releaseBlocked;
                    return (
                      <div
                        key={sprint.id}
                        role="cell"
                        {...inBlock(RELEASE_ROW, sprint.id)}
                        className={cx(
                          hoursCell,
                          'group/cell gap-2',
                          columnTint(sprint.id),
                          inBlock(RELEASE_ROW, sprint.id).className,
                        )}
                      >
                        {/* Only on hover, so the row reads as its figures until you reach for one. */}
                        {offered && (heldSoFar > 0 || releasedNow(sprint.id) > 0) ? (
                          <button
                            type="button"
                            // A word, not a control, and at once; a plain link's underline, not the kit's floating one.
                            className="cursor-pointer text-xs font-medium text-primary opacity-0 outline-none hover:underline focus-visible:underline focus-visible:opacity-100 group-hover/cell:opacity-100"
                            aria-label={`${retention.releaseLabel}, ${t('budget.sprintNumber', { number: sprint.number })}`}
                            onPointerDown={event => event.stopPropagation()}
                            onClick={() => setReleasing(sprint.id)}
                          >
                            {retention.releaseLabel}
                          </button>
                        ) : null}
                        {/* The link is gone while the grid has an edit, so the hover says why instead. */}
                        {canRelease &&
                        isOpenSprint(sprint.id) &&
                        releaseBlocked &&
                        (heldSoFar > 0 || releasedNow(sprint.id) > 0) ? (
                          <span className="text-xs text-muted-foreground opacity-0 group-hover/cell:opacity-100">
                            {t('spend.releaseDirty')}
                          </span>
                        ) : null}
                        <span className="whitespace-nowrap text-sm tabular-nums text-foreground">
                          {releasedNow(sprint.id) ? `${EXTRA_CHARGE}${formatEur(releasedNow(sprint.id))}` : ''}
                        </span>
                      </div>
                    );
                  })}
                  {totalCell(
                    RELEASE_ROW,
                    <span className="whitespace-nowrap text-sm tabular-nums text-foreground">
                      {overYear(releasedNow) ? `${EXTRA_CHARGE}${formatEur(overYear(releasedNow))}` : ''}
                    </span>,
                  )}
                </div>
              </>
            )}

            {showsRetention && retention?.onRelease && (
              <ReleaseDialog
                into={releasing}
                held={Math.max(heldSoFar, 0)}
                sprints={openSprints}
                released={retention.released}
                pendingBefore={pendingBefore}
                onRelease={retention.onRelease}
                onClose={() => setReleasing(null)}
              />
            )}

            {/* Everyone on the product, whichever chapter they are filed under. */}
            <div role="row" style={columns} className={hoursRow}>
              <div
                role="rowheader"
                {...inBlock(TOTAL_ROW, NAME_COL)}
                className={cx(hoursTotalName, 'border-b-0 font-bold', inBlock(TOTAL_ROW, NAME_COL).className)}
              >
                {t('budget.gridTotal')}
              </div>
              {shown.map(sprint => (
                <div
                  key={sprint.id}
                  role="cell"
                  {...inBlock(TOTAL_ROW, sprint.id)}
                  className={cx(
                    hoursCell,
                    'border-b-0 text-sm font-bold',
                    columnTint(sprint.id, true) || 'bg-muted/60',
                    inBlock(TOTAL_ROW, sprint.id).className,
                  )}
                >
                  {figures(workHours(sprint.id), gridCost(sprint.id), {
                    className: projectedInk(sprint.id) || undefined,
                  })}
                </div>
              ))}
              {totalCell(
                TOTAL_ROW,
                figures(overYear(workHours), overYear(gridCost)),
                cx(hoursEndStrong, 'border-b-0 text-sm font-bold'),
              )}
            </div>
          </div>
          <SprintScrollRail scroller={scroller} nameWidth={hoursNameWidth} columns={shown.length} />
        </div>
      )}

      {/* Where the other figure is asked for: the keys copy what is on screen, this copies the rest. */}
      <CellMenu
        at={range.menuAt}
        onClose={range.closeMenu}
        items={[
          ...(priced
            ? [
                { label: t('cells.copyHours'), onSelect: () => range.copyAs('hours') },
                { label: t('cells.copyCost'), onSelect: () => range.copyAs('cost') },
              ]
            : [{ label: t('cells.copy'), onSelect: () => range.copyAs('hours') }]),
          // The copy key asks which figure and nothing else: clearing a block is not what was pressed.
          ...(!range.menuIsCopy && range.hasEditable()
            ? [{ label: t('cells.clear'), onSelect: () => range.clearCells() }]
            : []),
        ]}
      />

      {range.costPasteRefused && (
        <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.costPasteRefused')}</p>
      )}
      {range.copyFailed && <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.copyFailed')}</p>}
    </section>
  );
};

export default WorkforceGrid;
